import React, { createContext, useContext, useEffect, useState } from "react";
import { dashboardDarkVars, dashboardLightVars } from "./dashboardTheme";

// The dashboard's light/dark toggle — deliberately simple: one global
// boolean, no prefers-color-scheme detection (not requested), no per-route
// theme. Scope of what actually responds to it is controlled entirely by
// where the caller applies `vars`/`theme` (see App.jsx: the inner
// main-content wrapper only, never the outer .subscene-dashboard root,
// so Sidebar structurally can't see this and stays dark).
const STORAGE_KEY = "subscene-dashboard-theme";

const DashboardThemeContext = createContext({
  theme: "light",
  vars: dashboardLightVars,
  toggleTheme: () => {},
});

export function DashboardThemeProvider({ children }) {
  // Read synchronously in the initializer (not a useEffect) so the first
  // render already has the right theme — avoids a flash of the wrong theme
  // on reload. Light is the default for anyone who hasn't chosen yet;
  // "dark" is the only value that opts back into it.
  const [theme, setTheme] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === "dark" ? "dark" : "light";
    } catch {
      return "light";
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // Non-fatal — the toggle still works for this session, it just won't
      // persist across a reload.
    }
  }, [theme]);

  const toggleTheme = () => setTheme((t) => (t === "light" ? "dark" : "light"));
  const vars = theme === "light" ? dashboardLightVars : dashboardDarkVars;

  return (
    <DashboardThemeContext.Provider value={{ theme, vars, toggleTheme }}>
      {children}
    </DashboardThemeContext.Provider>
  );
}

export const useDashboardTheme = () => useContext(DashboardThemeContext);
